import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { getBatchUploadLog } from '../../../api/services/BatchUploadFiles';

import { BATCH_UPLOAD_LOG_PAGE_1_QUERY_KEY, SHOULD_POLL_UPLOAD_LOG_KEY } from './queryKeys';

const POLL_INTERVAL_MS = 10 * 1000;

interface UploadRecord {
  id: number;
  completedAt?: Date | string | null;
}

// Detects a new row by watching the top (newest) row's completedAt change —
// rows only ever appear already-complete, there's no "processing" state.
// SHOULD_POLL_UPLOAD_LOG_KEY is reset to false whenever UploadsTable mounts, so a
// stale `true` from a previous visit can never keep this polling across remounts.
export const useUploadCompletionPolling = <T extends UploadRecord>(
  onPage1Updated: (updatedPage1Uploads: T[]) => void
): void => {
  const queryClient = useQueryClient();
  const latestKnownCompletedAtRef = useRef<string | Date | null | undefined>(undefined);

  const { data } = useQuery({
    queryKey: BATCH_UPLOAD_LOG_PAGE_1_QUERY_KEY,
    queryFn: ({ signal }) => getBatchUploadLog({ perPage: 20, page: 1, batchUploadFileType: 'rider' }, signal),
    refetchInterval: () => (queryClient.getQueryData<boolean>(SHOULD_POLL_UPLOAD_LOG_KEY) ? POLL_INTERVAL_MS : false),
  });

  useEffect(() => {
    if (data?.type !== 'success') {
      return;
    }

    const uploads = data.data as T[];
    const latestCompletedAt = uploads[0]?.completedAt ?? null;
    const isFirstObservation = latestKnownCompletedAtRef.current === undefined;
    const hasNewRow = !isFirstObservation && latestCompletedAt !== latestKnownCompletedAtRef.current;

    latestKnownCompletedAtRef.current = latestCompletedAt;
    onPage1Updated(uploads);

    if (hasNewRow) {
      queryClient.setQueryData(SHOULD_POLL_UPLOAD_LOG_KEY, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
};
